import React from "react";
import { WizardSchemaType } from "../index";
import { Box, Stack } from "@mui/material";
import { ImagePlaceholderSvg } from "@onair/ui/svg/image-placeholder";
import { EVENT_COLORS } from "@onair/ui/event/constants";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Iconify } from "@onair/minimal-ui/iconify";
import dayjs, {
	FORMAT_TIME,
	FORMAT_DATE_WITHOUT_YEAR,
} from "@onair/utils/dayjs";
import { formatCurrency } from "@onair/utils/format-number";
import { ExtendRoomProvider, getLabelFromClientRoomProvider } from "@onair/constants";
import { SessionPriceType } from "@onair/repositories";

interface PropTypes {
	data?: WizardSchemaType | null;
	item?: WizardSchemaType["stepTimeline"]["sessions"][0] | null;
	index?: number;
}

const EventSessionItemPreview: React.FC<PropTypes> = ({
	data = null,
	item = null,
	index = 0,
}) => {
	const colors = EVENT_COLORS;

	if (!item) {
		return (
			<Grid item xs={4}>
				<Stack spacing={0.75}>
					<Box
						className="w-full h-[100px] rounded-lg"
						display="flex"
						alignItems="center"
						justifyContent="center"
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
					>
						<ImagePlaceholderSvg size="md" />
					</Box>
					<Box
						className="w-full h-16 rounded-lg"
						display="flex"
						alignItems="center"
						justifyContent="center"
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
					/>
					<Box
						className="w-full h-5 rounded"
						display="flex"
						alignItems="center"
						justifyContent="center"
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
					/>
				</Stack>
			</Grid>
		);
	}

	const pricing = data.stepPricing?.sessions?.[index];

	const hasInformation =
		item.name ||
		(item.startDate && item.endDate) ||
		pricing?.roomProvider ||
		pricing?.isFree ||
		pricing?.farePrice ||
		(data?.stepPricing?.priceType === SessionPriceType.ALL &&
			(data?.stepPricing?.isFree || data?.stepPricing?.farePrice));

	const banner = React.useMemo(() => {
		if (item.banner) {
			return (
				<Box
					bgcolor={colors.bgColor}
					border={`1px solid ${colors.borderColor}`}
					overflow="hidden"
					className="w-full h-[100px] rounded-lg"
					sx={{
						backgroundImage: `url(${item.banner?.type ? URL.createObjectURL(item.banner as Blob) : item.banner})`,
						backgroundSize: "cover",
						backgroundPosition: "center",
						backgroundRepeat: "no-repeat",
					}}
				/>
			);
		}

		return (
			<Box
				className="w-full h-[100px] rounded-lg"
				display="flex"
				alignItems="center"
				justifyContent="center"
				bgcolor={colors.bgColor}
				border={`1px solid ${colors.borderColor}`}
			>
				<ImagePlaceholderSvg size="md" />
			</Box>
		);
	}, [item.banner]);

	const date = React.useMemo(() => {
		if (
			item.startDate &&
			item.endDate &&
			!dayjs(item.startDate).isSame(item.endDate, "d")
		) {
			return (
				<Stack direction="row" spacing={1} alignItems="center">
					<Stack spacing={0}>
						<Typography
							flex={1}
							variant="subtitle1"
							align="left"
							color={colors.color}
							fontSize={9}
						>
							{item.endDate && (
								<>
									{dayjs(item.startDate).format(FORMAT_TIME)},{" "}
									{dayjs(item.startDate).format(FORMAT_DATE_WITHOUT_YEAR)} -{" "}
									{dayjs(item.endDate).format(FORMAT_TIME)},{" "}
									{dayjs(item.endDate).format(FORMAT_DATE_WITHOUT_YEAR)}
								</>
							)}
						</Typography>
					</Stack>
				</Stack>
			);
		}
		return (
			<Stack direction="row" spacing={1} alignItems="center">
				<Stack spacing={0}>
					<Typography
						flex={1}
						variant="subtitle1"
						align="left"
						color={colors.color}
						fontSize={9}
					>
						{item.endDate && (
							<>
								{dayjs(item.startDate).format(FORMAT_TIME)} -{" "}
								{dayjs(item.endDate).format(FORMAT_TIME)},{" "}
								{dayjs(item.startDate).format(FORMAT_DATE_WITHOUT_YEAR)}
							</>
						)}
					</Typography>
				</Stack>
			</Stack>
		);
	}, [item.startDate, item.endDate]);

	return (
		<Grid item xs={4}>
			<Stack spacing={0.75}>
				{banner}
				{hasInformation ? (
					<Stack spacing={0.5}>
						{item.name ? (
							<Typography
								fontWeight={600}
								sx={{
									wordBreak: "break-word",
								}}
								color={colors.color}
								variant="caption"
							>
								{item.name}
							</Typography>
						) : (
							<Box
								className="w-full h-2 rounded"
								display="flex"
								alignItems="center"
								justifyContent="center"
								bgcolor={colors.bgColor}
								border={`1px solid ${colors.borderColor}`}
							/>
						)}
						{item.startDate ? (
							<Stack spacing={0.5} direction="row" alignItems="center">
								<Iconify
									width={10}
									height={10}
									icon="solar:clock-circle-bold"
									color={colors.color}
								/>
								{date}
							</Stack>
						) : (
							<Box
								className="w-full h-2 rounded"
								display="flex"
								alignItems="center"
								justifyContent="center"
								bgcolor={colors.bgColor}
								border={`1px solid ${colors.borderColor}`}
							/>
						)}
						{pricing?.roomProvider ? (
							<Stack spacing={0.5} direction="row" alignItems="center">
								<Iconify
									width={10}
									height={10}
									icon="solar:videocamera-record-bold"
									color={colors.color}
								/>
								<Typography
									flex={1}
									variant="subtitle1"
									align="left"
									color={colors.color}
									fontSize={9}
								>
									{getLabelFromClientRoomProvider(pricing.roomProvider)}
								</Typography>
							</Stack>
						) : (
							<Box
								className="w-full h-2 rounded"
								display="flex"
								alignItems="center"
								justifyContent="center"
								bgcolor={colors.bgColor}
								border={`1px solid ${colors.borderColor}`}
							/>
						)}
						{pricing?.farePrice ||
						pricing?.isFree ||
						(data?.stepPricing?.priceType === SessionPriceType.ALL &&
							(data?.stepPricing?.isFree || data?.stepPricing?.farePrice)) ? (
							<Stack spacing={0.5} direction="row" alignItems="center">
								<Iconify
									width={10}
									height={10}
									icon="solar:wad-of-money-bold"
									color={colors.color}
								/>
								<Typography
									flex={1}
									variant="subtitle1"
									align="left"
									color={colors.color}
									fontSize={9}
								>
									{data?.stepPricing?.priceType === SessionPriceType.ALL
										? "--"
										: pricing?.isFree
											? "Miễn phí"
											: formatCurrency(pricing?.farePrice)}
								</Typography>
							</Stack>
						) : (
							<Box
								className="w-full h-2 rounded"
								display="flex"
								alignItems="center"
								justifyContent="center"
								bgcolor={colors.bgColor}
								border={`1px solid ${colors.borderColor}`}
							/>
						)}
					</Stack>
				) : (
					<Box
						className="w-full h-16 rounded-lg"
						display="flex"
						alignItems="center"
						justifyContent="center"
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
					/>
				)}
				{hasInformation || item.banner ? (
					<Box
						display="flex"
						className="w-full h-5 rounded"
						alignItems="center"
						justifyContent="center"
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
					>
						<Typography
							color={colors.color}
							variant="caption"
							fontWeight="bold"
							textAlign="center"
							fontSize="10px"
						>
							Chọn
						</Typography>
					</Box>
				) : (
					<Box
						className="w-full h-5 rounded"
						display="flex"
						alignItems="center"
						justifyContent="center"
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
					/>
				)}
			</Stack>
		</Grid>
	);
};

export default EventSessionItemPreview;
